/* eslint-disable no-unused-expressions */
import React, { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';
import { getQuizEventLocalStorageId, QUIZ_SLICK_TIMEOUT } from '@/src/constants';
import { useWindowWidth } from '@hooks/windowHooks';
import { breakpoints } from '@hooks/useBreakpoints';
import cloneDeep from 'lodash/cloneDeep';
import Bugsnag from '@bugsnag/js';
import PropTypes from 'prop-types';
import SharedLibs from 'wizarding-world-web-shared';
import {
  GET_QUIZ_STATE_WITH_RESULTS,
  SAVE_QUIZ_STATE,
  SUBMIT_QUIZ,
  GET_AGGREGATE_QUIZ_RESULT,
} from '@utils/graphQueries';
import Slider from 'react-slick';
import { useUserContext } from '@contexts/user-context';
import { QUIZ_URL } from '@/src/constants/routes';
import { trackEvent } from '@utils/analytics';
import { analyticsTags } from '@utils/index';
import QuizSplash from './components/QuizSplash';
import QuizTimer from './components/QuizTimer';
import QuizQuestion from './components/QuizQuestion';
import QuizStats from './components/QuizStats';
import CloseModal from './components/CloseModal';
import Image from '../Image';
import close from './imgs/close.svg';
import { fetchContent } from '../../utils/contentfulHelpers';
import s from './Quiz.module.scss';

const propTypes = {
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string,
      difficulty: PropTypes.oneOf(['Easy', 'Medium', 'Difficult']),
      correctAnswers: PropTypes.arrayOf(PropTypes.string),
      incorrectAnswers: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  title: PropTypes.string,
  summary: PropTypes.string,
  date: PropTypes.string,
  coverImage: PropTypes.string,
  contentfulId: PropTypes.string,
  contentTypeId: PropTypes.string,
  tags: PropTypes.array,
  displayTags: PropTypes.array,
  articleContentfulId: PropTypes.string,
  secondsToAnswer: PropTypes.number,
  isEvent: PropTypes.bool,
  eventWeek: PropTypes.string,
  eventCardIndex: PropTypes.number,
  onStart: PropTypes.func,
  onComplete: PropTypes.func,
};

const getChallengeId = (tags) => {
  if (!tags || !tags.length) return '';
  const challengeTag = tags.find((tag) => tag?.name.startsWith('challenge-'));
  return challengeTag ? challengeTag?.name.substring(10) : '';
};

const Quiz = ({
  questions = [],
  title = '',
  summary = '',
  date = '',
  coverImage = '',
  contentfulId = '',
  contentTypeId = '',
  tags = [],
  displayTags = [],
  articleContentfulId = '',
  secondsToAnswer = 30,
  isEvent = false,
  eventWeek = null,
  eventCardIndex = null,
  onStart = null,
  onComplete = null,
}) => {
  const { api: wizardingWorldApi } = SharedLibs.services;
  const { loading, isLoggedIn, loadingProfile, profile } = useUserContext();
  const router = useRouter();

  const [timeIsUp, setTimeIsUp] = useState(false);
  const [isAnimationStopped, setIsAnimationStopped] = useState(false);
  const secondCount = useRef(null);
  const fullscreenRef = useRef(null);
  const quizSlider = useRef(null);
  const retakeKey = useRef(0);

  const [isLoadingQuizState, setIsLoadingQuizState] = useState(true);
  const [fullscreenMode, setFullscreenMode] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [relatedQuizzes, setRelatedQuizzes] = useState([]);

  const [isFirstAttempt, setIsFirstAttempt] = useState(false);
  const [quizSessionId, setQuizSessionId] = useState(uuidv4());
  const [quizState, setQuizState] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizEnded, setQuizEnded] = useState(false);
  const [houseScores, setHouseScores] = useState([
    { houseName: 'gryffindor', houseScore: 0 },
    { houseName: 'ravenclaw', houseScore: 0 },
    { houseName: 'hufflepuff', houseScore: 0 },
    { houseName: 'slytherin', houseScore: 0 },
  ]);

  const width = useWindowWidth();
  const isMobile = width < breakpoints.md;
  const imageSet = [
    {
      src: close,
    },
  ];

  useEffect(() => {
    if (isMobile && quizStarted) {
      setFullscreenMode(true);
      if (currentQuestionIndex === 0) {
        fullscreenRef.current.scrollTop = 0;
      }
    } else {
      setFullscreenMode(false);
    }
  }, [currentQuestionIndex, isMobile, quizStarted, isEvent]);

  useEffect(() => {
    if (fullscreenMode) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [fullscreenMode]);

  useEffect(() => {
    const fetchMoreQuizzes = async () => {
      const [fetchData, errors] = await fetchContent({
        contentTypes: ['article'],
        count: 3,
        tags: ['quiz'],
        excludeIds: [articleContentfulId],
      });

      /* istanbul ignore else */
      if (fetchData?.length && !errors) {
        setRelatedQuizzes(
          fetchData.map((item) => {
            return {
              id: item.body.contentfulId,
              title: item.body.displayTitle,
              byLine: item.body.author.title,
              image: item.body.mainImage.image.file.url,
              externalId: item.body.externalId,
            };
          }),
        );
      }
    };
    fetchMoreQuizzes();
  }, []);

  // Find the display tag that defines the quiz level
  let quizLevel = 'medium';
  if (displayTags) {
    displayTags?.forEach((tag) => {
      if (tag?.name?.match(/^(easy|medium|difficult)-quiz$/)) {
        quizLevel = tag?.name?.split('-')?.[0];
      }
    });
  }

  const getScore = (answeredQuestions) => {
    let score = 0;
    answeredQuestions.forEach((answeredQuestion) => {
      const matchingQuestion = questions.find((q) => {
        return q.contentfulId === answeredQuestion.quizQuestionEntryId;
      });

      if (
        matchingQuestion?.correctAnswers?.some((correctAnswer) =>
          answeredQuestion.answers.includes(correctAnswer),
        )
      ) {
        score += 1;
      }
    });

    return score;
  };

  const trackQuizEvent = (eventName, extraProperties = {}) => {
    const properties = {
      content_id: contentfulId,
      content_name: title,
      content_type: contentTypeId,
      content_tags: analyticsTags(tags),
      content_display_tag: analyticsTags(displayTags),
      activation_date: date,
      question_count: questions.length,
      session_id: quizSessionId,
      ...extraProperties,
    };

    if (quizState?.answeredQuestions?.length > 0) {
      properties.score = getScore(quizState?.answeredQuestions);
    }

    trackEvent(eventName, properties);
  };

  const saveQuizResult = async () => {
    /* istanbul ignore next */
    if (!isLoggedIn) return;

    try {
      const input = {
        quizEntryId: contentfulId,
        hogwartsHouse: profile.hogwartsHouse,
        answeredQuestions: quizState.answeredQuestions,
      };
      if (isEvent) input.quizChallengeId = getChallengeId(tags);
      await wizardingWorldApi().mutate({
        mutation: SUBMIT_QUIZ,
        variables: { input },
      });
    } catch (err) {
      // istanbul ignore next
      Bugsnag.notify(err, (event) => {
        event.addMetadata('quiz', {
          quizEntryId: contentfulId,
          answeredQuestions: quizState.answeredQuestions,
        });
      });
    }
  };

  // Load average house scores to be used in leaderboard
  useEffect(() => {
    /* istanbul ignore next */
    if (!contentfulId || !wizardingWorldApi || !questions?.length > 0) return;

    const getHouseScores = async (quizEntryId) => {
      try {
        const { data } = await wizardingWorldApi().query({
          query: GET_AGGREGATE_QUIZ_RESULT,
          variables: { quizEntryId },
        });

        // istanbul ignore else
        if (data?.aggregatedQuizResult) {
          const tempHouseScores = [];
          ['gryffindor', 'hufflepuff', 'ravenclaw', 'slytherin'].forEach((house) => {
            const correctResponses = data.aggregatedQuizResult[house].correct;
            const totalResponses = data.aggregatedQuizResult[house].total;
            tempHouseScores.push({
              houseName: house,
              houseScore: (correctResponses / totalResponses) * questions.length || 0,
              correctResponses,
              totalResponses,
            });
          });

          tempHouseScores.sort((house1, house2) => house2.houseScore - house1.houseScore);
          setHouseScores(tempHouseScores);
        }
      } catch (err) {
        // use default empty quiz averages
        // istanbul ignore next
        Bugsnag.notify(err, (event) => {
          event.addMetadata('quiz', {
            quizEntryId: contentfulId,
          });
        });
      }
    };

    getHouseScores(contentfulId);
  }, [contentfulId, wizardingWorldApi, questions]);

  // Load last saved quiz state for logged in users
  useEffect(() => {
    /* istanbul ignore next */
    if (!contentfulId || !wizardingWorldApi || loading) return;

    const getQuizState = async (quizEntryId) => {
      const initQuizState = {
        quizEntryId: contentfulId,
        answeredQuestions: [],
      };

      if (!isLoggedIn) {
        setQuizState(initQuizState);
        setIsLoadingQuizState(false);
        return;
      }

      try {
        const { data } = await wizardingWorldApi().query({
          query: GET_QUIZ_STATE_WITH_RESULTS,
          variables: { quizEntryId },
          fetchPolicy: 'network-only', // ignore apollo cache and always receive the latest state on the server
        });

        // Determine if a user has taken this quiz before. This informs whether we should append their score
        // to aggregate scores.
        setIsFirstAttempt(!data?.quizResult);

        // Determine where to start the user in the quiz based on already answered questions
        let lastAnsweredIndex = null;
        questions.forEach((question, i) => {
          const answeredQuestion = data?.quizState?.answeredQuestions?.find(
            (aq) => aq.quizQuestionEntryId === question.contentfulId,
          );
          if (answeredQuestion) {
            lastAnsweredIndex = i;
          }
        });
        setCurrentQuestionIndex(lastAnsweredIndex !== null ? lastAnsweredIndex + 1 : 0);

        // Strip out unneccessary data from api response quiz state to use in future
        // save state calls.
        // istanbul ignore else
        if (data.quizState) {
          initQuizState.answeredQuestions = data?.quizState?.answeredQuestions?.map(
            (answeredQuestion) => {
              return {
                quizQuestionEntryId: answeredQuestion.quizQuestionEntryId,
                answers: answeredQuestion.answers,
                duration: answeredQuestion.duration,
              };
            },
          );
        }
      } catch {
        // use default empty quiz state
      }

      setQuizState(initQuizState);
      setIsLoadingQuizState(false);
    };

    getQuizState(contentfulId);
  }, [contentfulId, wizardingWorldApi, loading]);

  // Save quiz state to the backend when it is updated
  useEffect(() => {
    if (loading || loadingProfile || (!loading && !isLoggedIn) || !quizStarted) return;

    const saveQuizState = async () => {
      // Do not save state if the user just started the quiz.
      if (quizState.answeredQuestions.length === 0) {
        return;
      }

      // Do not save state if the user just finished the quiz. Instead, save the results.
      if (quizState.answeredQuestions.length >= questions.length) {
        await saveQuizResult();
        return;
      }

      try {
        const input = {
          hogwartsHouse: profile.hogwartsHouse,
          quizEntryId: contentfulId,
          answeredQuestions: quizState.answeredQuestions,
        };
        if (isEvent) input.quizChallengeId = getChallengeId(tags);
        await wizardingWorldApi().mutate({
          mutation: SAVE_QUIZ_STATE,
          variables: { input },
        });
      } catch (err) {
        // istanbul ignore next
        Bugsnag.notify(err, (event) => {
          event.addMetadata('quiz', {
            quizEntryId: contentfulId,
            answeredQuestions: quizState.answeredQuestions,
          });
        });
      }
    };

    saveQuizState();
  }, [
    quizState,
    loading,
    isLoggedIn,
    loadingProfile,
    profile,
    contentfulId,
    questions.length,
    quizStarted,
    wizardingWorldApi,
  ]);

  useEffect(() => {
    if (quizEnded) {
      if (isEvent && onComplete) {
        onComplete({ score: getScore(quizState.answeredQuestions) });
      }
      trackQuizEvent('Quiz Completed', {
        answer_count: questions.length,
        quiz_completed: true,
        duration: quizState?.answeredQuestions?.reduce((sum, answer) => {
          return sum + answer.duration;
        }, 0),
      });
    }
  }, [quizEnded]);

  const handleQuizEnd = () => {
    setTimeout(() => {
      quizSlider.current?.slickNext();
    }, QUIZ_SLICK_TIMEOUT);
    // adjust aggregate scores if this is the user's first attempt
    setHouseScores((currentHouseScores) => {
      if (!profile?.hogwartsHouse || !isFirstAttempt) return currentHouseScores;

      const adjustedHouseScores = cloneDeep(currentHouseScores);
      const userScore = getScore(quizState.answeredQuestions);
      const userHouse = adjustedHouseScores.find(
        (score) => score.houseName === profile?.hogwartsHouse?.toLowerCase(),
      );

      // istanbul ignore next
      userHouse.houseScore =
        ((userHouse.correctResponses + userScore) / (userHouse.totalResponses + questions.length)) *
          questions.length || 0;

      adjustedHouseScores.sort((house1, house2) => house2.houseScore - house1.houseScore);
      return adjustedHouseScores;
    });

    setQuizEnded(true);
  };

  const handleRetakeQuiz = () => {
    setCurrentQuestionIndex(0);
    setTimeout(() => {
      quizSlider.current.slickGoTo(1);
    }, QUIZ_SLICK_TIMEOUT);
    const newSessionId = uuidv4();
    setQuizSessionId(newSessionId);
    trackQuizEvent('Quiz Started', { session_id: newSessionId });

    setQuizState({
      quizEntryId: contentfulId,
      answeredQuestions: [],
    });
    setIsFirstAttempt(false);
    setQuizStarted(true);
    if (isEvent) onStart();
    setQuizEnded(false);
    setIsAnimationStopped(false);
    retakeKey.current += 1;
  };

  const handleAfterChange = () => {
    setIsAnimationStopped(false);
  };

  const sliderSettings = {
    slidesToShow: 1,
    slidesToScroll: 1,
    dots: false,
    infinite: false,
    useCSS: true,
    easing: 'ease-in-out',
    variableWidth: false,
    variableHeight: true,
    arrows: false,
    accessibility: false,
    autoplaySpeed: 200,
    lazyLoad: false,
    pauseOnHover: false,
    pauseOnFocus: false,
    speed: 700,
    focusOnSelect: false,
    swipe: false,
    touchMove: false,
    afterChange: () => handleAfterChange(),
  };

  const handleNextQuestion = async (goToNext = true, goToSlide) => {
    if (currentQuestionIndex + 1 === questions.length && goToNext) {
      handleQuizEnd();
    } else {
      const increment = goToNext ? 1 : 0;
      const nextIndex = currentQuestionIndex + increment;
      setCurrentQuestionIndex(nextIndex);
      setQuizStarted(true);
      if (isEvent) onStart();

      trackQuizEvent('Quiz Question Displayed', {
        question_description: questions?.[nextIndex]?.displayTitle,
        question_index: nextIndex + 1, // segment index is 1 based
      });
      // without this the state updates sometimes cut off the animation
      if (goToSlide) {
        setTimeout(() => {
          quizSlider.current.slickGoTo(goToSlide, true);
        }, QUIZ_SLICK_TIMEOUT);
      } else {
        setTimeout(() => {
          quizSlider.current?.slickNext();
        }, QUIZ_SLICK_TIMEOUT);
      }
    }
  };

  const handleAnswer = async (question, answer) => {
    setIsAnimationStopped(true);
    setQuizState((currentQuizState) => {
      const newQuizState = cloneDeep(currentQuizState);
      newQuizState.answeredQuestions.push({
        quizQuestionEntryId: question.contentfulId,
        answers: [answer],
        duration: Math.round(secondCount.current),
      });

      return newQuizState;
    });

    trackQuizEvent('Quiz Question Answered', {
      question_description: question.displayTitle,
      question_answer: answer,
      question_index: currentQuestionIndex + 1, // segment index is 1 based
      is_correct: question.correctAnswers.includes(answer),
      duration: Math.round(secondCount.current),
    });
  };

  const handleExitRouter = () => {
    if (isEvent) {
      localStorage.setItem(getQuizEventLocalStorageId(), eventCardIndex);
      router.reload();
    } else {
      router.push(QUIZ_URL);
    }
  };

  const handleCloseQuiz = () => {
    if (quizStarted && !quizEnded) {
      setShowCloseModal(true);
      // TODO: pause animation timer when modal is shown
    } else {
      if (!isEvent) setFullscreenMode(false);
      handleExitRouter();
    }
  };

  const handleTimerCount = (count) => {
    secondCount.current = count;
  };

  const handleCancelClick = () => {
    setShowCloseModal(false);
  };

  const handleExitClick = () => {
    trackQuizEvent('Quiz Ended', {
      answer_count: quizState?.answeredQuestions?.length || 0,
      quiz_completed: false,
      duration:
        quizState?.answeredQuestions?.reduce((sum, answer) => {
          return sum + answer.duration;
        }, 0) || 0,
    });
    setFullscreenMode(false);
    setShowCloseModal(false);
    handleExitRouter();
  };

  const handleQuizStart = () => {
    if (
      quizState?.answeredQuestions?.length > 0 &&
      quizState?.answeredQuestions?.length < questions.length
    ) {
      trackQuizEvent('Quiz Resumed', {
        answer_count: quizState?.answeredQuestions?.length,
      });
      handleNextQuestion(false, quizState.answeredQuestions.length + 1);
    } else {
      trackQuizEvent('Quiz Started');
      handleNextQuestion(false);
    }
  };

  return (
    <div
      ref={fullscreenRef}
      className={[s.root, fullscreenMode ? s.fullscreen : ''].join(' ')}
      // this key reloads child components when updated (on quiz retake)
      key={retakeKey.current}
    >
      {quizStarted && fullscreenMode && (
        <button
          className={s.closeButton}
          type="button"
          name="Close quiz"
          onClick={() => handleCloseQuiz()}
        >
          <span>Close</span>
          <Image imageSet={imageSet} alt="close" />
        </button>
      )}
      <Slider {...sliderSettings} ref={quizSlider}>
        <QuizSplash
          level={quizLevel}
          title={title}
          summary={summary}
          date={date}
          questionCount={questions.length}
          coverImage={coverImage}
          isLoading={isLoadingQuizState}
          buttonText={
            quizState?.answeredQuestions?.length > 0 &&
            quizState?.answeredQuestions?.length < questions.length
              ? 'resume quiz'
              : 'start quiz'
          }
          isEvent={isEvent}
          eventWeek={eventWeek}
          quizStart={handleQuizStart}
        />
        {questions.map((question, index) => {
          return (
            <QuizTimer
              key={question}
              onNextQuestion={handleNextQuestion}
              onUnanswered={() => {
                handleAnswer(questions[index], 'out of time');
              }}
              isAnimationStopped={isAnimationStopped}
              setIsAnimationStopped={(stopped) => setIsAnimationStopped(stopped)}
              timeIsUp={currentQuestionIndex === index && timeIsUp}
              setTimeIsUp={(timeUp) => setTimeIsUp(timeUp)}
              secondsToAnswer={secondsToAnswer}
              isModalActive={currentQuestionIndex === index && showCloseModal}
              setTimer={(count) => handleTimerCount(count)}
              isReadyToShow={currentQuestionIndex === index && quizStarted && !quizEnded}
              quizStarted={quizStarted}
            >
              <QuizQuestion
                totalQuestions={questions.length}
                questionNumber={index + 1}
                currentQuestion={questions[index]}
                onNextQuestion={handleNextQuestion}
                onAnswer={handleAnswer}
                timeIsUp={timeIsUp}
              />
            </QuizTimer>
          );
        })}
        {quizEnded && (
          <QuizStats
            totalQuestions={questions.length}
            userScore={getScore(quizState.answeredQuestions)}
            houseScores={houseScores}
            retakeQuiz={handleRetakeQuiz}
            relatedQuizzes={relatedQuizzes}
            isEvent={isEvent}
          />
        )}
      </Slider>
      {showCloseModal && (
        <CloseModal onCancelClick={handleCancelClick} onExitClick={handleExitClick} />
      )}
    </div>
  );
};

Quiz.propTypes = propTypes;
export default Quiz;
